import moment from 'moment-timezone';
import {
	encodeUTCAdjustedDate,
	formatDateForParam,
	myTimeZone,
	timeZoneOfDate,
} from '../../utils/DataFormatterUtils';
import {
	ComparisonState,
	comparisonStateToQueryParams,
	queryParamsToComparisonState,
} from '../../utils/AnalyticsComparisonUtils';
import {
	formatDurationShort,
	formatSupplierName,
	parseSafeFloat,
	toMinutes,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import {
	DEFAULT_COMPLETED_AT_SPAN_MONTHS,
	DEFAULT_MIN_WO_COUNT,
	SUPPLIER_PERFORMANCE_CHART_KEY,
} from './SupplierPerformanceConstants';

export interface SupplierPerformanceFilters {
	locationIds?: string;
	supplierFacilityIds?: string;
	problemTypeIds?: string;
	woPriorityIds?: string;
	isPM?: 'true' | 'false';
	startDate?: moment.Moment;
	endDate?: moment.Moment;
	workCreatedAtStartDate?: moment.Moment;
	workCreatedAtEndDate?: moment.Moment;
	timeZone?: string;
	workCreatedAtTimeZone?: string;
}

export interface SupplierPerformanceRow {
	supplierFacilityId: number;
	supplierFacilityName: string;
	woCount: number;
	avgResponseTimeSeconds: number | null;
	avgResolutionTimeSeconds: number | null;
	avgResponseTimeMinutes: number;
	avgResolutionMinutes: number;
	firstTimeFixRate: number | null;
	isInternalTeam: boolean;
	hasChartMetrics: boolean;
	avgResponseTimeSecondsDisplay: any;
	avgResolutionTimeSecondsDisplay: any;
	firstTimeFixRateDisplay: any;
}

export const getDefaultSupplierPerformanceFilters = (): SupplierPerformanceFilters => ({
	locationIds: undefined,
	supplierFacilityIds: undefined,
	problemTypeIds: undefined,
	woPriorityIds: undefined,
	isPM: 'false',
	startDate: moment().subtract(DEFAULT_COMPLETED_AT_SPAN_MONTHS, 'months').startOf('day'),
	endDate: moment().endOf('day'),
	workCreatedAtStartDate: undefined,
	workCreatedAtEndDate: undefined,
});

export const supplierPerformanceStateToQueryParams = (
	filters: SupplierPerformanceFilters,
	minWoCount: number,
	showSupplierLabels: boolean,
	suppliersPanelOpen: boolean,
	comparisonState: ComparisonState | null
): Record<string, any> => {
	const params: any = {};
	if (filters.locationIds) {
		params.locationIds = filters.locationIds;
	}
	if (filters.supplierFacilityIds) {
		params.supplierFacilityIds = filters.supplierFacilityIds;
	}
	if (filters.problemTypeIds) {
		params.problemTypeIds = filters.problemTypeIds;
	}
	if (filters.woPriorityIds) {
		params.woPriorityIds = filters.woPriorityIds;
	}
	if (filters.isPM !== undefined) {
		params.isPM = filters.isPM;
	}
	if (filters.startDate && filters.endDate) {
		params.startDate = filters.startDate.format('YYYY-MM-DD');
		params.endDate = filters.endDate.format('YYYY-MM-DD');
	}
	if (filters.workCreatedAtStartDate && filters.workCreatedAtEndDate) {
		params.workCreatedAtStartDate = filters.workCreatedAtStartDate.format('YYYY-MM-DD');
		params.workCreatedAtEndDate = filters.workCreatedAtEndDate.format('YYYY-MM-DD');
	}
	if (minWoCount !== DEFAULT_MIN_WO_COUNT) {
		params.minWoCount = minWoCount;
	}
	if (showSupplierLabels) {
		params.labels = 'true';
	}
	if (!suppliersPanelOpen) {
		params.panel = 'false';
	}
	Object.assign(
		params,
		comparisonStateToQueryParams(SUPPLIER_PERFORMANCE_CHART_KEY, comparisonState)
	);
	return params;
};

export const queryParamsToSupplierPerformanceComparison = (
	queryParams: Record<string, any>
): ComparisonState => queryParamsToComparisonState(SUPPLIER_PERFORMANCE_CHART_KEY, queryParams);

export const supplierPerformanceFiltersToApiParams = (
	filters: SupplierPerformanceFilters
): Record<string, any> => {
	const params: any = {};
	const completedAtTimeZone = filters.timeZone || timeZoneOfDate(filters.startDate) || myTimeZone();
	const createdAtTimeZone =
		filters.workCreatedAtTimeZone || timeZoneOfDate(filters.workCreatedAtStartDate) || myTimeZone();
	if (filters.startDate && filters.endDate) {
		params.completedAtStartDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.startDate, false, completedAtTimeZone)
		);
		params.completedAtEndDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.endDate, true, completedAtTimeZone)
		);
	}
	if (filters.workCreatedAtStartDate && filters.workCreatedAtEndDate) {
		params.createdAtStartDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.workCreatedAtStartDate, false, createdAtTimeZone)
		);
		params.createdAtEndDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.workCreatedAtEndDate, true, createdAtTimeZone)
		);
	}
	if (filters.locationIds) {
		params.locationIds = filters.locationIds;
	}
	if (filters.supplierFacilityIds) {
		params.supplierFacilityIds = filters.supplierFacilityIds;
	}
	if (filters.problemTypeIds) {
		params.problemTypeIds = filters.problemTypeIds;
	}
	if (filters.woPriorityIds) {
		params.woPriorityIds = filters.woPriorityIds;
	}
	if (filters.isPM !== undefined) {
		params.isPM = filters.isPM;
	}
	return params;
};

export const normalizeSupplierPerformanceRows = (rawRows: any[]): SupplierPerformanceRow[] => {
	if (!rawRows || rawRows.length === 0) return [];
	return rawRows.map((row) => {
		const avgResponseTimeSeconds = parseSafeFloat(row && row.avgResponseTimeSeconds, null);
		const avgResolutionTimeSeconds = parseSafeFloat(row && row.avgResolutionTimeSeconds, null);
		const hasChartMetrics =
			avgResponseTimeSeconds !== null &&
			avgResponseTimeSeconds > 0 &&
			avgResolutionTimeSeconds !== null &&
			avgResolutionTimeSeconds > 0;
		return {
			...row,
			supplierFacilityId: row.supplierFacilityId,
			supplierFacilityName: formatSupplierName(row),
			woCount: row.woCount || 0,
			avgResponseTimeSeconds,
			avgResolutionTimeSeconds,
			avgResponseTimeMinutes: toMinutes(
				avgResponseTimeSeconds === null ? 0 : avgResponseTimeSeconds
			),
			avgResolutionMinutes: toMinutes(
				avgResolutionTimeSeconds === null ? 0 : avgResolutionTimeSeconds
			),
			firstTimeFixRate: parseSafeFloat(row && row.firstTimeFixRate, null),
			isInternalTeam: !!(row && row.isInternalTeam),
			hasChartMetrics,
			avgResponseTimeSecondsDisplay: row && row.avgResponseTimeSeconds,
			avgResolutionTimeSecondsDisplay: row && row.avgResolutionTimeSeconds,
			firstTimeFixRateDisplay: row && row.firstTimeFixRate,
		};
	});
};

export const formatFirstTimeFixRate = (value: any): string => {
	if (value === '-') return '-';
	const parsed = parseFloat(String(value));
	if (isNaN(parsed)) return '';
	return `${Math.round(parsed * 10) / 10}%`;
};

export const formatDurationCell = (value: any): string => {
	if (value === '-') return '-';
	const parsed = parseFloat(String(value));
	if (isNaN(parsed) || parsed <= 0) return '';
	return formatDurationShort(parsed);
};
